'use client';

import Link from 'next/link';
import { VoteButton } from './VoteButton';
import { StatusBadge } from './StatusBadge';
import { Badge } from '@/components/ui/badge';

export interface RequestCardData {
  id: string;
  title: string;
  description: string | null;
  status: string | null;
  voteCount: number | null;
  createdAt: Date | number | null;
  hasVoted: boolean;
  visitor?: { name: string | null; email: string } | null;
  category?: { name: string; color: string | null; slug: string } | null;
  commentCount?: number;
}

interface RequestCardProps {
  request: RequestCardData;
  portalSlug: string;
  onVote?: () => void;
  onAuthRequired?: () => void;
}

function timeAgo(date: Date | number | null): string {
  if (!date) return '';
  const now = Date.now();
  const then = typeof date === 'number' ? date : date.getTime();
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
}

export function RequestCard({
  request,
  portalSlug,
  onVote,
  onAuthRequired,
}: RequestCardProps) {
  const displayName = request.visitor?.name || request.visitor?.email || 'Anonymous';
  const createdAt = request.createdAt
    ? typeof request.createdAt === 'number'
      ? new Date(request.createdAt)
      : request.createdAt
    : null;

  return (
    <div className="flex gap-4 rounded-xl border border-border/60 bg-card p-4 hover:shadow-md hover:border-primary/20 transition-all duration-200">
      <VoteButton
        requestId={request.id}
        portalSlug={portalSlug}
        initialCount={request.voteCount ?? 0}
        initialHasVoted={request.hasVoted}
        onAuthRequired={onAuthRequired}
      />
      <div className="flex-1 min-w-0">
        <Link
          href={`/p/${portalSlug}/${request.id}`}
          className="text-base font-medium text-foreground hover:text-primary transition-colors"
        >
          {request.title}
        </Link>
        {request.description && (
          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
            {request.description}
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {request.status && <StatusBadge status={request.status} />}
          {request.category && (
            <Badge
              variant="outline"
              className="font-normal"
              style={request.category.color ? { borderColor: request.category.color, color: request.category.color } : undefined}
            >
              {request.category.name}
            </Badge>
          )}
          <span>by {displayName}</span>
          {/* timeAgo() reads Date.now(), so the server's value and the client's
              differ whenever the SSR→hydrate gap crosses a bucket boundary
              ("just now"→"1m ago", "29d"→"1mo", …). With ~30 cards on a portal
              that is a routine hydration text mismatch (React #418). */}
          <span suppressHydrationWarning>{timeAgo(createdAt)}</span>
          {request.commentCount !== undefined && request.commentCount > 0 && (
            <span className="flex items-center gap-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
              {request.commentCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
